import { useState } from 'react';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { ApiError } from '../../../../lib/apiError';
import { usersApi } from '../usersApi';
import type { UserDetail } from '../userTypes';

/**
 * Switching the second-factor PIN requirement on or off for one account.
 *
 * **Turning it OFF is the destructive direction**, so that is the one that takes the ceremony:
 * it removes a factor from somebody else's account, and the reader doing it is not the account
 * holder. Turning it on only adds a step to their next sign-in.
 */
export function PinRequirementDialog({
  user,
  onClose,
  onDone,
}: {
  user: UserDetail | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  if (!user) {
    return null;
  }

  // The dialog always proposes the OPPOSITE of the current state — it is reached from a card
  // that shows what the state is, so there is nothing to choose, only something to confirm.
  const turningOn = !user.pinRequired;

  async function run() {
    if (!user) {
      return;
    }

    setRunning(true);
    setError(null);

    try {
      await usersApi.setPinRequirement(user.id, turningOn);
      onDone();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError('Could not change the PIN requirement.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <ConfirmDialog
      open
      onClose={onClose}
      onConfirm={() => void run()}
      title={turningOn ? 'Require a sign-in PIN' : 'Stop requiring a sign-in PIN'}
      danger={!turningOn}
      requireConfirm={!turningOn}
      requireCredential={!turningOn}
      loading={running}
      id="user-pin-requirement-dialog"
      body={
        <div className="flex flex-col gap-2">
          <p>{bodyFor(user, turningOn)}</p>
          {error ? (
            <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
              {error.message}
            </p>
          ) : null}
        </div>
      }
    />
  );
}

/**
 * Says what will actually happen, which differs by whether the account already holds a PIN —
 * a reader switching this on needs to know nobody has to set anything up for them.
 */
function bodyFor(user: UserDetail, turningOn: boolean): string {
  const name = user.displayName;

  if (!turningOn) {
    return `${name} will sign in with their password alone. Any PIN they have chosen is kept, `
      + 'so switching this back on will ask for the same one.';
  }

  return user.hasPin
    ? `${name} will be asked for their existing sign-in PIN after their password, from their next sign-in.`
    : `${name} will be asked to choose a sign-in PIN the next time they sign in, and for it every time after. `
      + 'Nobody else sets it — they choose it themselves, and it is never shown to anyone.';
}
